<template>
  <div
    ref="headerObject"
    class="document-list-header"
  >
    <list-header
      ref="listHeader"
      :class="['_list-header', { '-border': showBorder }]"
      :tabs-shown="tabsShown"
      @set-header-tight="(val) => headerTight = val"
    >
      <template #left>
        <saved-view-tabs
          v-if="showViewsSelector"
          id="view-selector"
          ref="savedViewTabs"
          :right-element-width="rightElementWidth"
          :header-width="headerWidth"
          :header-tight="headerTight"
          :show-create="showCreateView"
          :shared-view-order="sharedViewOrder"
          :allowed-view-types="allowedViewTypes"
          :view-application="currentView.viewApplication"
          @tabs-shown-updated="(val) => tabsShown = val"
          @open-editor="openMenu"
        />
      </template>
      <template
        #right
      >
        <div
          ref="right"
        >
          <m-slots-container
            v-model:show-menu="showMenu"
            :slots="slotsWithDefaults"
          >
            <template #[SLOTS.DATA_LOADING]>
              <m-icon
                v-if="dataLoading"
                type="loading"
                spin
                :color="$colors.grey.lighten1"
                :style="{ marginRight: '.8rem' }"
              />
            </template>
            <template #[SLOTS.LOCKED]>
              <m-tooltip
                v-if="viewsService.context.readOnly.value"
              >
                <m-btn
                  icon="lock"
                  small
                  light
                  hide-border
                  disabled
                >
                  {{ $t('documentListHeader.locked') }}
                </m-btn>
                <template #title>
                  {{ $t('documentListHeader.lockedHint') }}
                </template>
              </m-tooltip>
            </template>
            <template #[SLOTS.CYCLE_SELECTOR]>
              <goal-cycle-app-settings-selector
                :in-card-menu="slotIsInMenu(SLOTS.CYCLE_SELECTOR)"
                :selected="selectedGoalCycles"
                :is-cycle-locked-on-view="isCycleLockedOnView"
                :change-selected-goal-cycles="changeSelectedGoalCycles"
                :lock-cycle-on-view="lockCycleOnView"
                :read-only="cycleSelectorReadOnly"
                :show-lock-cycle-option="showViewsSelector"
                with-unassigned
              />
            </template>
            <template #[SLOTS.GROUPING]>
              <component :is="$store.state.breakpoint.smAndDown ? 'm-card-item' : 'div'">
                <m-select
                  :items="groupingItems"
                  :value="currentView.params.grouping"
                  :disabled="viewsService.context.readOnly.value"
                  small
                  light
                  hide-border
                  :hide-hover="$store.state.breakpoint.smAndDown"
                  @change="updateViewParams($event, 'grouping')"
                />
              </component>
            </template>
            <template
              #[SLOTS.STRING_FILTER]
            >
              <list-string-filter
                ref="stringFilter"
                small
                @input="$emit('string-filter-changed', $event)"
              />
            </template>
            <template
              #[SLOTS.FILTER]
            >
              <list-filter
                :filter-props="filterProps"
                :has-value="filter.children.length > 0"
                :has-applied-value="filterHasAppliedValue"
                :has-unsaved-changes="viewsService.currentViewUnsavedChanges.value.includes('filter')"
                :views-service="viewsService"
                :disabled="viewsService.context.readOnly.value && filter.children.length === 0"
              />
            </template>
            <template
              #[SLOTS.SORTER]
            >
              <list-sorter
                :sort-options="sortOptions"
                :has-value="order.length > 0"
                :has-unsaved-changes="viewsService.currentViewUnsavedChanges.value.includes('order')"
                :disabled="viewsService.context.readOnly.value && order.length === 0"
              />
            </template>
            <template
              #[SLOTS.FILTER_IN_MENU]
            >
              <menu-list-filter
                :filter-props="filterProps"
                :has-dynamic-date-filters="hasDynamicDateFilters"
                :multi-model="multiModel"
                :views-service="viewsService"
                :disabled="viewsService.context.readOnly.value"
              />
            </template>
            <template
              #[SLOTS.SORTER_IN_MENU]
            >
              <menu-list-sorter
                :sort-options="sortOptions"
                :views-service="viewsService"
                :disabled="viewsService.context.readOnly.value"
              />
            </template>
            <template
              #[SLOTS.PROPERTIES]
            >
              <props-filter
                :in-card-menu="slotIsInMenu(SLOTS.PROPERTIES)"
                :value="props"
                :disabled="viewsService.context.readOnly.value"
                @input="updateViewParams($event, 'props')"
                @update:prop-order="updateViewParams($event, 'propOrder')"
              >
                <template #header>
                  <m-card-item
                    no-hover
                    :clickable="false"
                    :style="{ marginBottom: '.4rem' }"
                  >
                    <saved-view-info
                      :view="currentView"
                      :label="$t('savedViewInfo.propertyLabel')"
                    />
                  </m-card-item>
                </template>
              </props-filter>
            </template>
            <template
              #[SLOTS.COMPARE_TO]
            >
              <compare-to-previous
                :component="slotIsInMenu(SLOTS.COMPARE_TO) ? 'card-item' : 'button'"
                :value="currentView.params.compare"
                :disabled="viewsService.context.readOnly.value"
                @input="updateViewParams($event, 'compare')"
              />
            </template>
            <template #[SLOTS.PROGRESS_DISPLAY_OPTION]>
              <goal-progress-menu
                :progress-display-option="progressDisplayOption"
                :disabled="viewsService.context.readOnly.value"
                @input="setProgressDisplayOption"
              />
            </template>
            <template
              #[SLOTS.EXPAND_BUTTONS]
            >
              <m-divider
                v-if="hasSlotsInMenuBefore(SLOTS.EXPAND_BUTTONS)"
                xxs
              />
              <m-card-item
                class="_expand-next-level"
                icon="plus-square"
                @click="handleExpandNextLevel"
              >
                {{ $t('documentListHeader.expandNextLevel') }}
              </m-card-item>
              <m-card-item
                class="_expand-all"
                icon="expand-plus"
                @click="handleExpandAll"
              >
                {{ $t('documentListHeader.expandAll') }}
              </m-card-item>
              <m-card-item
                class="_collapse-next-level"
                icon="minus-square"
                @click="handleCollapseNextLevel"
              >
                {{ $t('documentListHeader.collapseNextLevel') }}
              </m-card-item>
              <m-card-item
                class="_collapse-all"
                icon="expand-minus"
                @click="handleCollapseAll"
              >
                {{ $t('documentListHeader.collapseAll') }}
              </m-card-item>
            </template>
            <template
              #[SLOTS.CREATE]
            >
              <div class="_create">
                <slot name="create" />
              </div>
            </template>
            <template
              #[SLOTS.VIEW_TITLE_EDITOR]
            >
              <saved-view-title-editor
                :entity="currentView"
                :disabled="!viewsService.context.canEdit.value"
                @update="handleUpdateMetaData"
              />
            </template>
            <template
              #[SLOTS.LAYOUT]
            >
              <layout-menu
                :saved-view="currentView"
                :allowed-view-types="allowedViewTypes"
                :view-application="currentView.viewApplication"
                :read-only="viewsService.context.readOnly.value"
                :can-edit="viewsService.context.canEdit.value"
                @update="handleUpdateMetaData"
                @update-view-params="updateViewParams"
              />
            </template>
          </m-slots-container>
        </div>
      </template>
    </list-header>
    <view-toolbar
      v-if="!isPrint"
      ref="viewToolbar"
      :class="['_view-toolbar', { '-show-border': !showBorder }]"
      :views-service="viewsService"
      :can-filter="canFilter"
      :can-sort="canSort"
      :sort-options="sortOptions"
      :filter-props="filterProps"
      :multi-model="multiModel"
      :has-dynamic-date-filters="hasDynamicDateFilters"
    />
  </div>
</template>

<script>
import CardWidthSelector from '@/components/list/CardWidthSelector.vue';
import CompareToPrevious from '@/components/list/CompareToPrevious.vue';
import GoalCycleAppSettingsSelector from '@/components/goal/cycle/GoalCycleAppSettingsSelector.vue';
import GoalProgressMenu from '@/components/goal/GoalProgressMenu.vue';
import LayoutMenu from '@/components/list/LayoutMenu.vue';
import ListFilter from '@/components/list/ListFilter.vue';
import ListHeader from '@/components/list/ListHeader.vue';
import ListSorter from '@/components/list/ListSorter.vue';
import ListStringFilter from '@/components/list/ListStringFilter.vue';
import MenuListFilter from '@/components/list/MenuListFilter.vue';
import MenuListSorter from '@/components/list/MenuListSorter.vue';
import PropsFilter from '@/components/PropsFilter.vue';
import SavedViewInfo from '@/components/list/SavedViewInfo.vue';
import SavedViewTabs from '@/components/list/SavedViewTabs.vue';
import SavedViewTitleEditor from '@/components/list/SavedViewTitleEditor.vue';
import ScopeFilter from '@/components/filter/ScopeFilter.vue';
import ViewToolbar from '@/components/list/ViewToolbar.vue';
import useGoalProperty from '@/composables/property/goal-property';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import useLoggedInUserAccount from '@/composables/logged-in-user-account/logged-in-user-account';
import usePrintMode from '@/composables/export/print-mode';
import useViewParamsProps from '@/composables/view-params/view-params-props';
import { SLOTS, SLOTS_LIST, VIEWS_SERVICE, VIEW_EVENT_BUS } from '@/lib/constants';
import { computed, inject } from 'vue';
import { copy, shallowCopy } from 'shared/lib/copy';
import { goal as goalConfig } from 'shared/api/query/configs.json';
import { textByLang } from 'shared/lib/language';
import { treeHasValue } from '@/lib/filter/scope-tree';
import { viewApplication as viewApplicationConfig, viewType } from 'shared/constants.json';

export default {
  name: 'DocumentListHeader',
  props: {
    selectedGoalCycles: {
      type: Array,
      default: () => [],
    },
    isCycleLockedOnView: {
      type: Boolean,
      default: false,
    },
    changeSelectedGoalCycles: {
      type: Function,
      default: undefined,
    },
    lockCycleOnView: {
      type: Function,
      default: () => {
      },
    },
    dataLoading: {
      type: Boolean,
      default: false,
    },
    sortOptions: {
      type: Array,
      default: () => [],
    },
    showViewsSelector: {
      type: Boolean,
      default: false,
    },
    showCreateView: {
      type: Boolean,
      default: false,
    },
    showApplyFilterOnFirstLevelOnly: {
      type: Boolean,
      default: false,
    },
    allowedViewTypes: {
      type: Array,
      default: () => [],
    },
    filterProps: {
      type: Array,
      default: () => [],
    },
    hasDynamicDateFilters: {
      type: Boolean,
      default: false,
    },
    showSavedViewInfo: {
      type: Boolean,
      default: false,
    },
    multiModel: {
      type: Boolean,
      default: false,
    },
    slots: {
      type: Array,
      default: () => [],
      validator(slots) {
        return slots.every((slot) => SLOTS_LIST.includes(slot.name));
      },
    },
  },
  setup(componentProps) {
    const eventBus = inject(VIEW_EVENT_BUS);
    const viewsService = inject(VIEWS_SERVICE);
    const currentView = viewsService.currentView;
    const { loggedInUser, userLang } = useLoggedInUser();
    const { loggedInUserAccount } = useLoggedInUserAccount();
    const { statusProperty } = useGoalProperty();
    const { props } = useViewParamsProps(currentView);
    const { isPrint } = usePrintMode();

    const progressDisplayOption = computed(() => currentView.value.params.props.find((p) => p.key === goalConfig.edges.cachedCalculatedCurrent).progressDisplayOption);

    const canFilter = computed(() => componentProps.slots.some((s) => s.name === SLOTS.FILTER));
    const canSort = computed(() => componentProps.slots.some((s) => s.name === SLOTS.SORTER));

    const setProgressDisplayOption = (option) => {
      const props = viewsService.currentView.value.params.props;

      const propIndex = props.findIndex((p) => p.key === goalConfig.edges.cachedCalculatedCurrent);
      const res = shallowCopy(viewsService.currentView.value.params.props);
      res.splice(propIndex, 1, {
        ...props[propIndex],
        progressDisplayOption: option,
      });
      viewsService.setParams(viewsService.currentView.value, {
        ...viewsService.currentView.value.params,
        props: res,
      });
    };

    return {
      eventBus,
      isPrint,
      viewsService,
      currentView,
      loggedInUser,
      loggedInUserAccount,
      props,
      statusLabel: textByLang(statusProperty.value.label, userLang.value),
      progressDisplayOption,
      setProgressDisplayOption,
      canFilter,
      canSort,
    };
  },
  emits: ['string-filter-changed', 'expand-next-level', 'expand-all', 'collapse-next-level', 'collapse-all'],
  components: {
    ScopeFilter,
    CardWidthSelector,
    SavedViewInfo,
    ViewToolbar,
    CompareToPrevious,
    ListStringFilter,
    GoalCycleAppSettingsSelector,
    SavedViewTabs,
    ListHeader,
    PropsFilter,
    ListSorter,
    ListFilter,
    GoalProgressMenu,
    MenuListFilter,
    MenuListSorter,
    SavedViewTitleEditor,
    LayoutMenu,
  },
  data() {
    return {
      headerTight: false,
      showMenu: false,
      rightElementWidth: 0,
      headerWidth: 0,
      SLOTS,
      tabsShown: 0,
    };
  },
  computed: {
    showBorder() {
      return [viewType.list, viewType.feed, viewType.default].includes(this.currentView.viewType);
    },
    cycleSelectorReadOnly() {
      return this.isPrint || (this.isCycleLockedOnView && (this.viewsService.context.readOnly.value || this.changeSelectedGoalCycles === undefined));
    },
    groupingItems() {
      return [
        {
          text: this.$t('networkControls.colorByStatus', { statusLabel: this.statusLabel }),
          value: 'status',
          customColor: !this.$store.state.breakpoint.smAndDown ? {
            color: this.$colors.grey.base,
            background: 'transparent',
          } : undefined,
        },
        {
          text: this.$t('networkControls.progress'),
          value: 'progress',
          customColor: !this.$store.state.breakpoint.smAndDown ? {
            color: this.$colors.grey.base,
            background: 'transparent',
          } : undefined,
        },
      ];
    },
    filter() {
      if (this.currentView.params === undefined) {
        return null;
      }
      return this.currentView.params.filter;
    },
    filterHasAppliedValue() {
      return treeHasValue(this.filter);
    },
    order() {
      if (!Array.isArray(this.currentView.params?.order)) {
        return [];
      }
      return this.currentView.params.order;
    },
    sharedViewOrder() {
      return [
        viewApplicationConfig.goal,
        viewApplicationConfig.update,
        viewApplicationConfig.updateFeed,
        viewApplicationConfig.goalSpace,
        viewApplicationConfig.updateSpace,
        viewApplicationConfig.updateFeedSpace,
        viewApplicationConfig.insightSpace,
        viewApplicationConfig.memberSpace,
        viewApplicationConfig.gridPage,
        viewApplicationConfig.gridPageTileCheckin,
        viewApplicationConfig.gridPageTileGoal,
        viewApplicationConfig.gridPageTileUpdateFeed,
        viewApplicationConfig.gridPageTileMember,
      ].includes(this.currentView.viewApplication);
    },
    slotsWithDefaults() {
      let slots = [{ name: SLOTS.DATA_LOADING }, { name: SLOTS.LOCKED }, ...copy(this.slots)];

      if (this.$store.state.breakpoint.smAndDown) {
        slots.forEach((slot) => {
          if (slot.inMenu === undefined && ![SLOTS.DATA_LOADING, SLOTS.LOCKED, SLOTS.CYCLE_SELECTOR].includes(slot.name)) {
            slot.inMenu = true;
          }
        });
      }

      slots.forEach((slot) => {
        if (slot.inMenu === undefined && [
          SLOTS.PROPERTIES,
          SLOTS.COMPARE_TO,
          SLOTS.PROGRESS_DISPLAY_OPTION,
          SLOTS.EXPAND_BUTTONS,
        ].includes(slot.name)) {
          slot.inMenu = true;
        }
      });

      if (this.isPrint) {
        const res = [];
        const cycleSlot = slots.find((s) => s.name === SLOTS.CYCLE_SELECTOR);
        if (cycleSlot !== undefined && (cycleSlot.inMenu === undefined || cycleSlot.inMenu === false)) {
          res.push({ name: SLOTS.CYCLE_SELECTOR });
        }
        const compareSlot = slots.find((s) => s.name === SLOTS.COMPARE_TO);
        if (compareSlot !== undefined && (compareSlot.inMenu === undefined || compareSlot.inMenu === false)) {
          res.push({ name: SLOTS.COMPARE_TO });
        }
        return res;
      }

      if (this.headerTight === true) {
        slots.forEach((slot) => {
          if ([SLOTS.DATA_LOADING, SLOTS.CREATE].includes(slot.name)) {
            return;
          }
          slot.inMenu = true;
        });
      }

      let firstMenuSlotIndex = slots.findIndex((s) => s.inMenu === true);
      // Identify the need to create ellipsis menu
      if (firstMenuSlotIndex === -1 && this.showViewsSelector) {
        firstMenuSlotIndex = slots.length;
      }
      const propertiesSlotIndex = slots.findIndex((s) => s.name === SLOTS.PROPERTIES);
      const indexToInsert = Math.max(firstMenuSlotIndex, propertiesSlotIndex + 1);
      // If no ellipsis menu exists, we don't create one
      if (firstMenuSlotIndex > -1) {
        const sortIndex = slots.findIndex((s) => s.name === SLOTS.SORTER);
        if (sortIndex >= 0) {
          slots.splice(indexToInsert, 0, { name: SLOTS.SORTER_IN_MENU, inMenu: true });
        }
        const filterIndex = slots.findIndex((s) => s.name === SLOTS.FILTER);
        if (filterIndex >= 0) {
          slots.splice(indexToInsert, 0, { name: SLOTS.FILTER_IN_MENU, inMenu: true });
        }
        slots.splice(firstMenuSlotIndex, 0, { name: SLOTS.LAYOUT, inMenu: true });
        if (this.showViewsSelector) {
          slots.splice(firstMenuSlotIndex, 0, { name: SLOTS.VIEW_TITLE_EDITOR, inMenu: true });
        }
      }

      slots = slots.filter((s) => !([SLOTS.FILTER, SLOTS.SORTER, SLOTS.STRING_FILTER].includes(s.name) && s.inMenu === true));

      return slots;
    },
  },
  methods: {
    handleUpdateMetaData(view) {
      this.viewsService.updateView(view);
      this.$refs.savedViewTabs.reOrderTabs();
    },
    slotIsInMenu(slotName) {
      return this.slotsWithDefaults.some((s) => s.name === slotName && s.inMenu === true);
    },
    hasSlotsInMenuBefore(slotName) {
      return this.slotsWithDefaults.filter((s) => s.inMenu === true).findIndex((s) => s.name === slotName) !== 0;
    },
    handleExpandNextLevel() {
      this.$emit('expand-next-level');
      this.showMenu = false;
    },
    handleExpandAll() {
      this.$emit('expand-all');
      this.showMenu = false;
    },
    handleCollapseNextLevel() {
      this.$emit('collapse-next-level');
      this.showMenu = false;
    },
    handleCollapseAll() {
      this.$emit('collapse-all');
      this.showMenu = false;
    },
    updateViewParams(value, key) {
      this.viewsService.setParams(
        this.currentView,
        {
          ...this.currentView.params,
          [key]: value,
        },
      );
    },
    openMenu() {
      this.showMenu = true;
    },
    openStringFilter() {
      this.$refs.stringFilter.show();
    },
  },
  mounted() {
    this.eventBus.$on('applyText', this.openStringFilter);
    this.rightElementWidth = this.$refs.right.getBoundingClientRect().width;
    this.resizeObserver = new ResizeObserver(() => {
      this.rightElementWidth = this.$refs.right.getBoundingClientRect().width;
    });
    this.resizeObserver.observe(this.$refs.right);

    this.headerWidth = this.$refs.headerObject.getBoundingClientRect().width;
    this.headerSizeObserver = new ResizeObserver(() => {
      this.headerWidth = this.$refs.headerObject.getBoundingClientRect().width;
    });
    this.headerSizeObserver.observe(this.$refs.headerObject);
  },
  beforeUnmount() {
    this.resizeObserver.disconnect();
    this.headerSizeObserver.disconnect();
    this.eventBus.$off('applyText', this.openStringFilter);
  },
};
</script>

<style
    scoped
    lang="scss"
    type="text/scss"
>
.document-list-header {
  // needs deep because ListHeader has several root elements
  :deep(._list-header) {
    padding-bottom: 0.6rem;
    padding-top: 0.6rem;

    &.-border {
      border-bottom: 1px solid $border-color;
    }

    ._right {
      display: flex;
      margin-left: auto;

      ._create {
        margin-left: .4rem;
      }
    }
  }

  ._view-toolbar {
    // View toolbar top border is needed whenever on cascade, tree and network
    &.-show-border {
      border-top: 1px solid $border-color;
    }
  }
}
</style>
